import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import {
	GithubAuthorizeSchema,
	GithubConfigurationSchema,
	GithubConnectionSchema,
	GithubDisconnectSchema,
	GithubMappingRemovalSchema,
	GithubMappingSchema,
	GithubOrgMembersResponse,
	GithubOrgMembersResponseSchema,
	GithubPreferencesSchema,
	GithubReposResponseSchema,
	GithubStatusSchema,
} from '../schemas/github';

const headers = {
	'Key-Inflection': 'camel',
};

const githubKeys = {
	all: ['github'] as const,
	status: () => [...githubKeys.all, 'status'] as const,
	connection: () => [...githubKeys.all, 'connection'] as const,
	configuration: () => [...githubKeys.all, 'configuration'] as const,
	searchRepos: (q: string) => [...githubKeys.all, 'searchRepos', q] as const,
	orgMembers: (params: GithubOrgMembersParams) => [...githubKeys.all, 'orgMembers', params] as const,
};

export const GITHUB_ORG_MEMBERS_PER_PAGE = 25;

type GithubOrgMembersParams = {
	org?: string;
	page?: number;
	perPage?: number;
};

const githubPaths = {
	status: () => '/github_connector/status',
	authorize: () => '/github_connector/authorize',
	disconnect: () => '/github_connector/disconnect',
	searchRepos: () => '/github_connector/search_repos',
	preferences: () => '/github_connector/preferences',
	connection: () => '/admin/bizy/company/github/connection',
	configuration: () => '/admin/bizy/company/github/configuration',
	configurationConnect: () => '/admin/bizy/company/github/configuration/connect',
	orgMembers: () => '/admin/bizy/company/github/members',
	mappings: () => '/admin/bizy/company/github/mappings',
	mapping: (githubId: number) => `/admin/bizy/company/github/mappings/${githubId}`,
};

export const useGithubStatus = (enabled = true) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.status(),
		queryFn: async () => {
			const res = await http.get(githubPaths.status(), { headers });
			return GithubStatusSchema.parse(res.data);
		},
		enabled,
	});
};

// Admin-only: the calling admin's personal GitHub connection (per-user token).
// Used ONLY to decide whether to show the "connect your GitHub" CTA so this
// admin can become the company connector — it does NOT bound the members list
// (that's the company config's selected_orgs; see useGithubConfiguration).
export const useGithubConnection = (enabled = true) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.connection(),
		queryFn: async () => {
			const res = await http.get(githubPaths.connection(), { headers });
			return GithubConnectionSchema.parse(res.data);
		},
		enabled,
		retry: false,
	});
};

// Company-level connector configuration (BIZY-527): the company scope the
// connector dreams on, the org-dreaming toggle, and the recorded company
// credential. Reads/writes are separate from the per-user connection above.
export const useGithubConfiguration = (enabled = true) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.configuration(),
		queryFn: async () => {
			const res = await http.get(githubPaths.configuration(), { headers });
			return GithubConfigurationSchema.parse(res.data);
		},
		enabled,
		retry: false,
	});
};

type GithubConfigurationUpdate = {
	// Partial update — omit a key to leave it unchanged; send an empty array to
	// clear a list. snake_case because only responses are camelized.
	selected_orgs?: string[];
	selected_repos?: string[];
	org_dreaming_enabled?: boolean;
};

export const useUpdateGithubConfiguration = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (update: GithubConfigurationUpdate) => {
			const res = await http.patch(githubPaths.configuration(), update, { headers });
			return GithubConfigurationSchema.parse(res.data);
		},
		onSuccess: (config) => {
			queryClient.setQueryData(githubKeys.configuration(), config);
			// The company scope determines which orgs the connector reads, so a
			// scope change can grow or shrink the members the review list shows.
			queryClient.invalidateQueries({ queryKey: [...githubKeys.all, 'orgMembers'] });
		},
	});
};

// Records the calling admin's GitHub token as the company credential. 409
// (github_not_connected) means the admin must connect their own GitHub first —
// callers should route to the per-user connect flow and retry.
export const useConnectGithubCompany = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const res = await http.post(githubPaths.configurationConnect(), {}, { headers });
			return GithubConfigurationSchema.parse(res.data);
		},
		onSuccess: (config) => {
			queryClient.setQueryData(githubKeys.configuration(), config);
			queryClient.invalidateQueries({ queryKey: githubKeys.connection() });
		},
	});
};

export const useGithubAuthorize = () => {
	const http = useHttpClient();
	return useMutation({
		mutationFn: async (returnPath?: string) => {
			const res = await http.get(githubPaths.authorize(), {
				headers,
				params: returnPath ? { return_path: returnPath } : undefined,
			});
			return GithubAuthorizeSchema.parse(res.data);
		},
	});
};

export const useGithubDisconnect = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const res = await http.delete(githubPaths.disconnect(), { headers });
			return GithubDisconnectSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: githubKeys.all });
		},
	});
};

export const useGithubSearchRepos = (query: string) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.searchRepos(query),
		queryFn: async () => {
			const res = await http.get(githubPaths.searchRepos(), {
				headers,
				params: { q: query },
			});
			return GithubReposResponseSchema.parse(res.data);
		},
		enabled: query.length >= 2,
	});
};

export const useGithubUpdatePreferences = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (prefs: { selected_orgs: string[]; selected_repos: string[] }) => {
			const res = await http.put(githubPaths.preferences(), prefs, { headers });
			return GithubPreferencesSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: githubKeys.status() });
			queryClient.invalidateQueries({ queryKey: githubKeys.connection() });
			// NOTE: the company members list is scoped by the company config's
			// selected_orgs (see useGithubConfiguration), not by these per-user
			// preferences, so changing preferences does not invalidate orgMembers.
		},
	});
};

// Admin-only: lists members of a single GitHub org (server-paginated) and how
// each was auto-matched to a Bizy member. Paging is driven by `hasMore` — there
// is no total count. Pass `enabled: false` (e.g. until an org is selected and
// the admin's GitHub connection is confirmed) to avoid a guaranteed 409.
export const useGithubOrgMembers = ({ org, page = 1, perPage = GITHUB_ORG_MEMBERS_PER_PAGE }: GithubOrgMembersParams = {}, enabled = true) => {
	const http = useHttpClient();
	return useQuery({
		queryKey: githubKeys.orgMembers({ org, page, perPage }),
		queryFn: async () => {
			const res = await http.get(githubPaths.orgMembers(), {
				headers,
				// Request params are snake_case; `org` lists a single org at a time.
				params: { page, per_page: perPage, ...(org ? { org } : {}) },
			});
			return GithubOrgMembersResponseSchema.parse(res.data);
		},
		enabled,
		retry: false,
		// Keep the previous page visible while the next page loads so paging
		// doesn't flash the skeleton on every click.
		keepPreviousData: true,
	});
};

// Patches a single member across every cached org-members page so the row
// reflects a mapping change immediately, without waiting for a refetch.
const patchCachedMember = (queryClient: ReturnType<typeof useQueryClient>, githubId: number, patch: Partial<GithubOrgMembersResponse['members'][number]>) => {
	queryClient.setQueriesData<GithubOrgMembersResponse>([...githubKeys.all, 'orgMembers'], (prev) =>
		prev ? { ...prev, members: prev.members.map((member) => (member.githubId === githubId ? { ...member, ...patch } : member)) } : prev
	);
};

export const useCreateGithubMapping = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ userId, githubId, githubLogin }: { userId: string; githubId: number; githubLogin?: string }) => {
			const res = await http.post(
				githubPaths.mappings(),
				// Request bodies use snake_case; only responses are camelized by the header.
				{ user_id: userId, github_id: githubId, github_login: githubLogin },
				{ headers }
			);
			return GithubMappingSchema.parse(res.data);
		},
		onSuccess: (mapping) => {
			// Write the confirmed mapping straight into the cache so the row flips to
			// "mapped" the moment the request resolves. Confirming creates a durable
			// identity link, so the source becomes `existing_identity`.
			patchCachedMember(queryClient, mapping.githubId, { matchStatus: 'mapped', matchSource: 'existing_identity', bizyMember: mapping.bizyMember });
			// Reconcile in the background — e.g. remapping a person could unmatch
			// whoever previously held that Bizy member elsewhere in the org.
			queryClient.invalidateQueries({ queryKey: [...githubKeys.all, 'orgMembers'] });
		},
	});
};

export const useRemoveGithubMapping = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (githubId: number) => {
			const res = await http.delete(githubPaths.mapping(githubId), { headers });
			return GithubMappingRemovalSchema.parse(res.data);
		},
		onSuccess: (removal) => {
			if (removal.removed) {
				patchCachedMember(queryClient, removal.githubId, { matchStatus: 'unmatched', matchSource: null, bizyMember: null });
			}
			queryClient.invalidateQueries({ queryKey: [...githubKeys.all, 'orgMembers'] });
		},
	});
};
